import { useEffect, useMemo, useRef } from 'react';
import {
  addMonths,
  differenceInDays,
  eachMonthOfInterval,
  endOfDay,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { Loader2, BadgeAlert } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  useActivePlansTimeline,
  type ActivePlanBar,
  type UserActivePlansRow,
} from '@/api/analytics';

const USER_COL_WIDTH = 240;
const HEADER_HEIGHT = 36;
const ROW_HEIGHT = 56;
const BAR_HEIGHT = 14;
const BAR_GAP = 4;
const MAX_LANES = 3;
const DAY_WIDTH = 6; // pixels per day
const PAGE_LIMIT = 50;
const PREFETCH_THRESHOLD = 10; // rows-before-end at which we fetch next page

/** Assign each payment to a "lane" so overlapping bars stack vertically. */
function assignLanes(payments: ActivePlanBar[], axisEnd: Date): number[] {
  // Greedy: sort by start; place each in the first lane whose tail <= this start.
  const indexed = payments.map((p, i) => ({ p, i }));
  indexed.sort((a, b) =>
    a.p.createdAt.localeCompare(b.p.createdAt),
  );
  const laneEnds: number[] = []; // ms timestamp of each lane's last bar's end
  const result = new Array<number>(payments.length).fill(0);
  for (const { p, i } of indexed) {
    const start = new Date(p.createdAt).getTime();
    const end = p.expiresAt
      ? new Date(p.expiresAt).getTime()
      : axisEnd.getTime();
    let assigned = -1;
    for (let lane = 0; lane < laneEnds.length; lane++) {
      if (laneEnds[lane] <= start) {
        assigned = lane;
        break;
      }
    }
    if (assigned === -1) {
      if (laneEnds.length < MAX_LANES) {
        assigned = laneEnds.length;
        laneEnds.push(end);
      } else {
        // Out of lanes — clamp to the last lane; visually overlaps.
        assigned = MAX_LANES - 1;
        laneEnds[assigned] = Math.max(laneEnds[assigned], end);
      }
    } else {
      laneEnds[assigned] = end;
    }
    result[i] = assigned;
  }
  return result;
}

function barColor(p: ActivePlanBar): string {
  if (p.isExpired) {
    return 'bg-muted/60 border-muted-foreground/30 text-muted-foreground';
  }
  if (p.isClass) {
    return 'bg-purple-500/25 border-purple-500/70 text-purple-950 dark:text-purple-100';
  }
  if (p.isFixedPayment) {
    return 'bg-amber-500/25 border-amber-500/70 text-amber-950 dark:text-amber-100';
  }
  return 'bg-blue-500/25 border-blue-500/70 text-blue-950 dark:text-blue-100';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'yyyy-MM-dd');
  } catch {
    return iso.slice(0, 10);
  }
}

function ActivePlanBarView({
  payment,
  lane,
  axisStart,
  axisEnd,
  axisDays,
}: {
  payment: ActivePlanBar;
  lane: number;
  axisStart: Date;
  axisEnd: Date;
  axisDays: number;
}) {
  const created = parseISO(payment.createdAt);
  const expires = payment.expiresAt ? parseISO(payment.expiresAt) : null;

  // Clamp to axis
  let startDays = differenceInDays(created, axisStart);
  let endDays = expires
    ? differenceInDays(expires, axisStart)
    : axisDays; // open-ended → extend to axis end

  // If entirely outside axis, render nothing.
  if (endDays < 0 || startDays > axisDays) return null;

  startDays = Math.max(0, startDays);
  endDays = Math.min(axisDays, endDays);

  let widthDays = endDays - startDays;
  if (widthDays < 1) widthDays = 1;

  const left = startDays * DAY_WIDTH;
  const width = widthDays * DAY_WIDTH;
  const top = lane * (BAR_HEIGHT + BAR_GAP) + BAR_GAP;
  const isOpenEnded = !expires;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'absolute rounded-sm border text-xs flex items-center px-1.5 cursor-pointer overflow-hidden whitespace-nowrap hover:ring-2 ring-primary/40 transition-shadow',
            barColor(payment),
          )}
          style={{
            left,
            width,
            top,
            height: BAR_HEIGHT,
          }}
          title={payment.planName ?? 'Plan'}
        >
          <span className="truncate">
            {payment.planName ?? 'Plan'}
            {isOpenEnded && ' →'}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-1.5 text-sm">
          <div className="font-medium">
            {payment.planName ?? 'Unknown plan'}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <span className="text-muted-foreground">Created</span>
            <span className="font-mono">{fmtDate(payment.createdAt)}</span>
            <span className="text-muted-foreground">Expires</span>
            <span className="font-mono">
              {payment.expiresAt ? fmtDate(payment.expiresAt) : 'no end (fixed)'}
            </span>
            <span className="text-muted-foreground">Remaining entries</span>
            <span className="font-mono">
              {payment.remainingEntries === null
                ? 'unlimited'
                : payment.remainingEntries}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 pt-1">
            {payment.isExpired && (
              <Badge variant="secondary" className="text-xs">
                expired
              </Badge>
            )}
            {payment.isClass && (
              <Badge variant="outline" className="text-xs">
                class
              </Badge>
            )}
            {payment.isFixedPayment && (
              <Badge variant="outline" className="text-xs">
                fixed
              </Badge>
            )}
            {!payment.isClass && !payment.isFixedPayment && (
              <Badge variant="outline" className="text-xs">
                gym
              </Badge>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono pt-1">
            paymentId: {payment.paymentId}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MonthHeader({
  axisStart,
  axisDays,
}: {
  axisStart: Date;
  axisDays: number;
}) {
  const axisEnd = useMemo(
    () => new Date(axisStart.getTime() + axisDays * 24 * 60 * 60 * 1000),
    [axisStart, axisDays],
  );
  const months = useMemo(
    () => eachMonthOfInterval({ start: axisStart, end: axisEnd }),
    [axisStart, axisEnd],
  );
  return (
    <>
      {months.map((m) => {
        const offsetDays = differenceInDays(startOfMonth(m), axisStart);
        return (
          <div
            key={m.toISOString()}
            className="absolute top-0 h-full flex items-center text-xs text-muted-foreground border-l border-border/60 pl-1.5 select-none"
            style={{ left: offsetDays * DAY_WIDTH }}
          >
            {format(m, 'MMM yyyy')}
          </div>
        );
      })}
    </>
  );
}

function TodayLine({
  axisStart,
  axisDays,
}: {
  axisStart: Date;
  axisDays: number;
}) {
  const today = startOfDay(new Date());
  const offset = differenceInDays(today, axisStart);
  if (offset < 0 || offset > axisDays) return null;
  return (
    <div
      className="absolute top-0 bottom-0 w-px bg-red-500/70 pointer-events-none z-10"
      style={{ left: offset * DAY_WIDTH }}
      aria-hidden
    />
  );
}

export function ActivePlansTimeline() {
  const today = useMemo(() => startOfDay(new Date()), []);
  const axisStart = useMemo(() => startOfDay(subMonths(today, 6)), [today]);
  const axisEnd = useMemo(() => endOfDay(addMonths(today, 6)), [today]);
  const axisDays = useMemo(
    () => differenceInDays(axisEnd, axisStart),
    [axisStart, axisEnd],
  );
  const axisWidth = axisDays * DAY_WIDTH;

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useActivePlansTimeline(PAGE_LIMIT);

  const rows: UserActivePlansRow[] = useMemo(
    () => data?.pages.flatMap((p) => p.users) ?? [],
    [data],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  // Trigger next page when nearing the end of loaded rows.
  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    if (virtualItems.length === 0) return;
    const lastIndex = virtualItems[virtualItems.length - 1].index;
    if (
      lastIndex >= rows.length - PREFETCH_THRESHOLD &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [virtualItems, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Pre-assign lanes per row so we can size the row height if needed (kept fixed for now).
  const rowLanes = useMemo(
    () => rows.map((r) => assignLanes(r.payments, axisEnd)),
    [rows, axisEnd],
  );

  if (isLoading) {
    return (
      <div className="border rounded-md p-8 flex items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading active plans timeline…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border rounded-md p-4 text-sm text-destructive flex items-center gap-2">
        <BadgeAlert className="h-4 w-4" />
        {(error as Error)?.message || 'Failed to load timeline.'}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="border rounded-md p-8 text-sm text-muted-foreground text-center">
        No active users with gymPayments.
      </div>
    );
  }

  const totalHeight = virtualizer.getTotalSize();
  const innerWidth = USER_COL_WIDTH + axisWidth;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-500/25 border border-blue-500/70" />
          Gym access
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-purple-500/25 border border-purple-500/70" />
          Class access
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-500/25 border border-amber-500/70" />
          Fixed plan
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-muted border border-muted-foreground/30" />
          Expired
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-px h-3 bg-red-500/70" />
          Today
        </div>
        <span className="ml-auto">
          {rows.length} user{rows.length === 1 ? '' : 's'} loaded
          {hasNextPage ? '…' : ''}
        </span>
      </div>

      <div
        ref={scrollRef}
        className="border rounded-md overflow-auto bg-background"
        style={{ height: 600 }}
      >
        <div
          style={{
            width: innerWidth,
            position: 'relative',
          }}
        >
          {/* Sticky header row */}
          <div
            className="sticky top-0 z-20 bg-background border-b flex"
            style={{ height: HEADER_HEIGHT }}
          >
            <div
              className="sticky left-0 z-30 bg-background border-r flex items-center px-3 text-xs font-medium"
              style={{ width: USER_COL_WIDTH, height: HEADER_HEIGHT }}
            >
              User
            </div>
            <div
              className="relative"
              style={{ width: axisWidth, height: HEADER_HEIGHT }}
            >
              <MonthHeader axisStart={axisStart} axisDays={axisDays} />
              <TodayLine axisStart={axisStart} axisDays={axisDays} />
            </div>
          </div>

          {/* Virtualized rows */}
          <div
            style={{
              height: totalHeight,
              position: 'relative',
              width: innerWidth,
            }}
          >
            {virtualItems.map((virt) => {
              const row = rows[virt.index];
              const lanes = rowLanes[virt.index];
              return (
                <div
                  key={row.userId}
                  className="absolute left-0 flex border-b"
                  style={{
                    top: virt.start,
                    height: virt.size,
                    width: innerWidth,
                  }}
                >
                  <div
                    className="sticky left-0 z-10 bg-background border-r px-3 py-2 text-xs flex flex-col justify-center"
                    style={{ width: USER_COL_WIDTH, height: virt.size }}
                  >
                    <div className="truncate font-medium">
                      {row.surname} {row.name}
                    </div>
                    <div className="truncate text-muted-foreground text-[10px]">
                      {row.email || '—'}
                    </div>
                  </div>
                  <div
                    className="relative"
                    style={{ width: axisWidth, height: virt.size }}
                  >
                    <TodayLine axisStart={axisStart} axisDays={axisDays} />
                    {row.payments.map((p, i) => (
                      <ActivePlanBarView
                        key={`${p.paymentId}-${i}`}
                        payment={p}
                        lane={lanes[i] ?? 0}
                        axisStart={axisStart}
                        axisEnd={axisEnd}
                        axisDays={axisDays}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isFetchingNextPage && (
        <div className="flex items-center justify-center text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
          Loading more users…
        </div>
      )}
    </div>
  );
}
